import { CSVNationalitiesType, CSVTownshipsType, useGeoInfo } from 'features/signup/signUpHooks';
import { PathName, getRouteByKey } from 'utility/routes';
import Persist, { StorageKind } from 'lib/persist';
import { PersonalInformation, RegistrationValue } from 'features/signup/types';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { SelectInput, SelectOptionType } from 'components/inputs/selectInput';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { gtag, isTruthy, myUnidecode } from 'utility/functions';
import {
  setFiscalCodeSignup,
  setIsCalculatedFiscalCode,
  setIsExternalResidencial,
  setIsExternalState,
  setIsSameAsResidence,
  setIsSpid,
  setPersonalData,
} from 'features/signup/signup';
import { stringNotEmpty, stringWithoutDigit } from 'lib/labels';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import {
  useLazyCalculateFiscalCodeQuery,
  useLazySignUpCheckFiscalCodeQuery,
  usePostSignUpDataMutation,
} from 'features/api/userSlice';

import { BannerInformation } from 'components/bannerInformation';
import { ButtonRegistrationWrapper } from '../buttonRegistrationWrapper';
import { DataInput } from 'components/inputs/dataInput';
import { KEY_COOKIE_SIGNUP } from './utils/constant';
import { REGISTRATION_STEP_KEY } from 'features/signup/constants';
import { RadioButton } from 'components/inputs/radioButton';
import { RegistrationStatus } from 'components/page-content/registrati/PageContent';
import { RichText } from 'components/richText';
import { SPIDButton } from 'components/spid/SPIDButton';
import { SimpleCheckbox } from 'components/inputs/simpleCheckbox';
import { TextInput } from 'components/inputs/textInput';
import { ToggleCheckBox } from 'components/inputs/toggleCheckBox';
import classNames from 'classnames';
import format from 'date-fns/format';
import personalDataStyle from './PersonalInfo.module.scss';
import { setCookie } from 'lib/persist/cookie';
import styles from 'components/page-content/registrati/registrati.module.scss';
import subYears from 'date-fns/subYears';
import { useGetLabelByKey } from 'hooks/useLingUI';
import { useRouter } from 'next/router';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

const localStorage = Persist(StorageKind.local);

export type PersonalDataLabel = {
  titlePersonalData: string;
  descriptionPersonalData: string;
  descriptionPromoCode: string;
  name: string;
  placeholderName: string;
  surname: string;
  placeholderSurname: string;
  gender?: string;
  genderValues: Array<{ label: string; value: 0 | 1 }>;
  county: string;
  city: string;
  fiscalCode: string;
  address: string;
  placeholderAddress: string;
  addressNumber: string;
  residenceCounty: string;
  residenceCity: string;
  zipCode: string;
  labelCalcolaCodiceFiscale: string;
  spidEnabled: boolean;
  spidTitle: string;
  spidDescription: string;
  addressDomicile: string;
  placeholderDomicileAddress: string;
  cityDomicile: string;
  sameAsDomicile: string;
  zipCodeDomicile: string;
  placeholderZipCodeDomicile: string;
  provinceDomicile: string;
  addressNumberDomicile: string;
  placeholderAddressNumberDomicile: string;
  birthday: string;
  externalState: string;
  nationality: string;
  birthNationality: string;
  externalResidential: string;
};
export type PersonalDataProps = {
  next: Dispatch<SetStateAction<boolean>>;
  back: Dispatch<SetStateAction<boolean>>;
  labels: PersonalDataLabel;
  setState: Dispatch<SetStateAction<RegistrationStatus>>;
};

const EXTERNAL_STATE = 'ESTERO';
const EMPTY_ITEM_DEFAULT = {
  value: 'default',
  isDisabled: true,
};

const DEFAULT_NATIONALITY = {
  ...EMPTY_ITEM_DEFAULT,
  label: 'Seleziona nazione',
};
const DEFAULT_NATIONALITY_SELECTION: Array<SelectOptionType> = [DEFAULT_NATIONALITY];

const DEFAULT_TOWNSHIP = {
  ...EMPTY_ITEM_DEFAULT,
  label: 'Seleziona città',
};
const DEFAULT_TOWNSHIP_SELECTION: Array<SelectOptionType> = [DEFAULT_TOWNSHIP];

const DEFAULT_MUNICIPALITY = {
  ...EMPTY_ITEM_DEFAULT,
  label: 'Seleziona provincia',
};
const DEFAULT_MUNICIPALITY_SLECTION: Array<SelectOptionType> = [DEFAULT_MUNICIPALITY];

export const PersonalInfo: React.FC<PersonalDataProps> = ({ next, back, labels, setState }) => {
  const { push, asPath } = useRouter();
  const dispatch = useAppDispatch();
  const [trigger] = useLazyCalculateFiscalCodeQuery();
  const [postSignUpData] = usePostSignUpDataMutation();
  const [signupCheck] = useLazySignUpCheckFiscalCodeQuery();

  const { getTownships, getNationalities } = useGeoInfo();

  const registrationUrl = useMemo(() => getRouteByKey(PathName.registrationUrl), []);

  const sessionData = useTypedSelector(({ signup }) => signup);

  const {
    cap,
    name,
    city,
    isSpid,
    gender,
    surname,
    address,
    spidCode,
    province,
    birthDate,
    fiscalCode,
    cityOfBirth,
    domicileCap,
    domicileCity,
    isExternalState,
    domicileAddress,
    provinceOfBirth,
    domicileProvince,
    isSameAsDomicile,
    isFiscalCodeCalculated,
    isExternalResidencial,
  } = sessionData ?? {};

  const [isFiscalCodeToggleVisible, setIsFiscalCodeToggleVisible] = useState(isFiscalCodeCalculated);

  const labelOppure = useGetLabelByKey('oppure')!;
  const labelCF = useGetLabelByKey('wrong-check-fiscal-code');

  const [nationalityCsv, setNationalityCsv] = useState<CSVNationalitiesType>();
  const [parsedCSVResponse, setParsedCSVResponse] = useState<CSVTownshipsType>();

  const [townships, setTownships] = useState<Array<SelectOptionType>>(DEFAULT_TOWNSHIP_SELECTION);
  const [nationality, setNationality] = useState<Array<SelectOptionType>>(DEFAULT_NATIONALITY_SELECTION);
  const [municipalities, setMunicipalities] = useState<Array<SelectOptionType>>(DEFAULT_MUNICIPALITY_SLECTION);
  const [townshipsOfBirth, setTownshipsOfBirth] = useState<Array<SelectOptionType>>(DEFAULT_TOWNSHIP_SELECTION);
  const [townshipsOfDomicile, setTownshipsOfDomicile] = useState<Array<SelectOptionType>>(DEFAULT_TOWNSHIP_SELECTION);

  const [errorFC, setErrorFC] = useState<string | null>(null);

  const getMunicipality = (obj: any, province: any) => {
    return obj.filter((item: any) => item.sigla === province).map((item: any) => item.comune);
  };

  const getLandRegistry = (obj: any, cityOfBirth: any) => {
    return obj.filter((item: any) => item.comune == cityOfBirth)[0]?.catasto;
  };

  const getNationalityCode = (obj: any, cityOfBirth: any) => {
    return obj.filter((item: any) => item.descrizione === cityOfBirth)[0]?.codice;
  };

  const schema = z.object({
    name: stringNotEmpty.pipe(stringWithoutDigit).refine((value) => /^[A-Za-zÀ-ÿ\s'-]+$/.test(value)),
    surname: stringNotEmpty.pipe(stringWithoutDigit),
    birthDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    gender: z.string().min(1),
    provinceOfBirth: z
      .string()
      .min(1)
      .refine((value) => value !== EMPTY_ITEM_DEFAULT.value),
    cityOfBirth: z
      .string()
      .min(1)
      .refine((value) => value !== EMPTY_ITEM_DEFAULT.value),
    fiscalCode: z
      .string()
      .min(16)
      .regex(
        /^(?:[A-Z][AEIOU][AEIOUX]|[AEIOU]X{2}|[B-DF-HJ-NP-TV-Z]{2}[A-Z]){2}(?:[\dLMNP-V]{2}(?:[A-EHLMPR-T](?:[04LQ][1-9MNP-V]|[15MR][\dLMNP-V]|[26NS][0-8LMNP-U])|[DHPS][37PT][0L]|[ACELMRT][37PT][01LM]|[AC-EHLMPR-T][26NS][9V])|(?:[02468LNQSU][048LQU]|[13579MPRTV][26NS])B[26NS][9V])(?:[A-MZ][1-9MNP-V][\dLMNP-V]{2}|[A-M][0L](?:[1-9MNP-V][\dLMNP-V]|[0L][1-9MNP-V]))[A-Z]$/i
      ),
    address: z.string().min(3),
    province: z
      .string()
      .min(1)
      .refine((value) => value !== EMPTY_ITEM_DEFAULT.value),
    city: z
      .string()
      .min(1)
      .refine((value) => value !== EMPTY_ITEM_DEFAULT.value),
    cap: stringNotEmpty.pipe(z.string().regex(/\d/).min(5).max(5)),
    dynamicInputs: z.array(
      z.object({
        isFiscalCodeCorrect: z.boolean().refine((value) => value),
      }),
      z.object({
        isExternalState: z.boolean().refine((value) => value),
      })
    ),
    dynamicDomicile: z.array(
      z.object({
        domicileAddress: stringNotEmpty.refine((value) => value !== EMPTY_ITEM_DEFAULT.value),
        domicileProvince: stringNotEmpty.refine((value) => value !== EMPTY_ITEM_DEFAULT.value),
        domicileCity: stringNotEmpty.refine((value) => value !== EMPTY_ITEM_DEFAULT.value),
        domicileCap: stringNotEmpty.pipe(z.string().regex(/\d/).min(5).max(5)),
      })
    ),
  });

  type SchemaValidationType = z.infer<typeof schema>;

  const { control, handleSubmit, watch, setValue, getValues, setError, clearErrors, formState } =
    useForm<SchemaValidationType>({
      resolver: zodResolver(schema),
      mode: 'onChange',
      reValidateMode: 'onChange',
      defaultValues: {
        cap: cap,
        name: name,
        city: city || EMPTY_ITEM_DEFAULT.value,
        gender: gender,
        surname: surname,
        address: address,
        province: province || EMPTY_ITEM_DEFAULT.value,
        birthDate: birthDate,
        fiscalCode: fiscalCode,
        cityOfBirth: cityOfBirth || EMPTY_ITEM_DEFAULT.value,
        dynamicInputs: isFiscalCodeCalculated ? [{ isFiscalCodeCorrect: true }] : [],
        provinceOfBirth: provinceOfBirth || EMPTY_ITEM_DEFAULT.value,
        dynamicDomicile: !isSameAsDomicile
          ? [
              {
                domicileCap: domicileCap!,
                domicileCity: domicileCity!,
                domicileAddress: domicileAddress!,
                domicileProvince: domicileProvince!,
              },
            ]
          : [],
      },
    });

  const { append } = useFieldArray<SchemaValidationType>({
    control,
    name: 'dynamicInputs',
  });

  const { append: appendDomicile, remove: removeDomicile } = useFieldArray({
    control,
    name: 'dynamicDomicile',
  });

  const goNext: SubmitHandler<SchemaValidationType> = async (data) => {
    const { dynamicDomicile } = data;
    const manualFiscalCode = data.fiscalCode;
    setErrorFC(null);

    try {
      clearErrors();

      const payload = Object.assign(data, dynamicDomicile?.[0] ?? {}, {
        name: myUnidecode(data.name),
        gender: data.gender as PersonalInformation['gender'],
        surname: myUnidecode(data.surname),
        isExternalState: isExternalState,
        isSameAsDomicile: isSameAsDomicile,
      });

      const { domicileAddress: address, domicileCity: city } = dynamicDomicile?.[0] ?? {};

      if (!!city) Reflect.set(payload, 'domicileCity', myUnidecode(city));
      if (!!address) Reflect.set(payload, 'domicileAddress', myUnidecode(address));

      dispatch(setPersonalData(payload));

      await signupCheck(manualFiscalCode).unwrap();

      dispatch(setFiscalCodeSignup(manualFiscalCode));

      if (isFiscalCodeToggleVisible) {
        dispatch(setIsCalculatedFiscalCode(true));
      }

      next(true);
    } catch (error) {
      console.log('error?.data?', error?.data);
      setErrorFC(
        (error?.data?.response?.descrizione || error?.data.descrizione) ?? 'Codice Fiscale già presente nel sistema'
      );
    }
  };

  const saveSessionData = async () => {
    const key = await postSignUpData(sessionData).unwrap();
    if (key) {
      setCookie(KEY_COOKIE_SIGNUP, key);
    }
  };

  useEffect(() => {
    getTownships().then((r) => setParsedCSVResponse(r));
    getNationalities().then((r) => setNationalityCsv(r));
  }, [getNationalities, getTownships]);

  useEffect(() => {
    if (parsedCSVResponse && municipalities.length === 1) {
      const municipalitiesWithoutDuplicates = [
        municipalities,
        parsedCSVResponse
          .map(({ provincia, sigla }) => ({ provincia, sigla }))
          .map((item) => ({
            value: item.sigla,
            label: item.provincia,
          }))
          .filter((v, i, a) => a.findIndex((v2) => v2.value === v.value) === i)
          .sort((a, b) => a.label.localeCompare(b.label)),
      ].flat(1);
      setMunicipalities(municipalitiesWithoutDuplicates);

      if (provinceOfBirth) {
        const townShipsWithoutDuplicate = getMunicipality(parsedCSVResponse, provinceOfBirth);

        setTownshipsOfBirth([
          ...DEFAULT_TOWNSHIP_SELECTION,
          ...townShipsWithoutDuplicate
            .map((item) => ({
              value: item,
              label: item,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        ]);
      }
      if (province) {
        const townShipsWithoutDuplicate = getMunicipality(parsedCSVResponse, province);
        setTownships([
          ...DEFAULT_TOWNSHIP_SELECTION,
          ...townShipsWithoutDuplicate
            .map((item) => ({
              value: item,
              label: item,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        ]);
      }
      if (!isSameAsDomicile && domicileProvince) {
        const townShipsWithoutDuplicate = getMunicipality(parsedCSVResponse, domicileProvince);
        setTownshipsOfDomicile([
          ...DEFAULT_TOWNSHIP_SELECTION,
          ...townShipsWithoutDuplicate
            .map((item) => ({
              value: item,
              label: item,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        ]);
      }
    }
  }, [municipalities, parsedCSVResponse, domicileProvince, isSameAsDomicile, province, provinceOfBirth]);

  useEffect(() => {
    if (nationalityCsv && nationalityCsv.length > 0) {
      setNationality([
        ...DEFAULT_NATIONALITY_SELECTION,
        ...nationalityCsv
          .map((item) => ({
            value: item.descrizione,
            label: item.descrizione,
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      ]);
    }
  }, [nationalityCsv]);

  useEffect(() => {
    if (asPath.includes(registrationUrl)) {
      const step = localStorage.getItem(REGISTRATION_STEP_KEY);
      if (step !== 'personal-info') {
        localStorage.setItem(REGISTRATION_STEP_KEY, 'personal-info');
        gtag(
          {
            section: 'RegistrationStepPersonalInfo',
            detail: 'RegistrationStepPersonalInfo',
            event: 'RegistrationStepPersonalInfo',
          },
          true
        );
      }
    }
  }, [registrationUrl, asPath]);

  const { isCalculateFiscalCodeDisabled } = useMemo(() => {
    const { name, surname, birthDate, gender, cityOfBirth, provinceOfBirth, dynamicInputs } = getValues();

    let isFiscalCodeCorrect = false;
    let isCalculateFiscalCodeDisabled = true;

    if (isTruthy(dynamicInputs.length)) {
      isFiscalCodeCorrect = dynamicInputs[0].isFiscalCodeCorrect;
    }

    if (!isFiscalCodeCorrect) {
      const isCalculateFiscalCodeEnabled =
        [name, surname, birthDate, gender].every((element) => !!element) &&
        [provinceOfBirth, cityOfBirth].every((element) => !!element && element !== EMPTY_ITEM_DEFAULT.value);
      isCalculateFiscalCodeDisabled = isCalculateFiscalCodeEnabled ? false : true;
    }

    return { isCalculateFiscalCodeDisabled };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch()]);

  const computeFiscalCode = async () => {
    const { name, surname, birthDate, gender, cityOfBirth } = getValues();
    append({ isFiscalCodeCorrect: false });
    try {
      const { fiscalCode } = await trigger({
        name: name,
        gender: gender === '2' ? 'F' : 'M',
        surname: surname,
        birthday: birthDate,
        catastoCode: isExternalState
          ? getNationalityCode(nationalityCsv, cityOfBirth)
          : getLandRegistry(parsedCSVResponse, cityOfBirth),
      }).unwrap();

      setValue('fiscalCode', fiscalCode);
      clearErrors('fiscalCode');
      setIsFiscalCodeToggleVisible(true);
    } catch (error) {
      setIsFiscalCodeToggleVisible(false);
      setError('fiscalCode', {
        message: 'Non è stato possibile recuperare il codice fiscale',
        type: 'custom',
      });
      console.error(error);
    }
  };

  return (
    <div className={styles.formContainer}>
      <BannerInformation
        visible={(isSpid && !!spidCode && spidCode !== 1024) || !!errorFC}
        text={!errorFC ? `Non è stato possibile procedere con la registrazione tramite SPID (${spidCode})` : errorFC}
        type="error"
        isDismissible
        className={personalDataStyle.spidError}
      />
      <div className={personalDataStyle.spidContainer} suppressHydrationWarning>
        <div className={personalDataStyle.spidTitleContainer} suppressHydrationWarning>
          <strong className={styles.title} suppressHydrationWarning>
            {labels.spidTitle}
          </strong>
          <div className={styles.subTitle} suppressHydrationWarning>
            {labels.spidDescription}
          </div>
        </div>
        {labels.spidEnabled && (
          /* TODO : move to feature toggle settings */
          <SPIDButton
            onClick={async (url) => {
              await dispatch(
                setIsSpid({
                  isSpid: true,
                  spidCode: 0, // FAKE CODE TO UNDERSTAND IF USER NAVIGATE TO SPID
                })
              );
              await saveSessionData();

              setState({ state: RegistrationValue.LOADING });

              push(url);
            }}
          />
        )}
      </div>
      <div className={styles.dividerContainer}>
        <hr className={styles.divider} /> <span>{labelOppure}</span>
        <hr className={styles.divider} />
      </div>
      <h2 className={styles.title}>{labels.titlePersonalData}</h2>
      <RichText containerClassName={styles.subTitle}>{labels.descriptionPersonalData}</RichText>
      <form onSubmit={handleSubmit(goNext)} className={styles.form}>
        <TextInput
          label={labels.name}
          control={control}
          name="name"
          placeholder={labels.placeholderName}
          hasMarginBottom={true}
        />
        <TextInput
          label={labels.surname}
          control={control}
          name="surname"
          placeholder={labels.placeholderSurname}
          hasMarginBottom={true}
        />
        <DataInput
          label={labels.birthday}
          name="birthDate"
          control={control}
          min={'1920-01-01'}
          max={format(subYears(new Date(), 18), 'yyyy-MM-dd')}
          onChange={(e) => {
            setValue('birthDate', e.target.value);
            setValue('dynamicInputs', []);
          }}
        />
        <div className={styles.genderContainer}>
          <label htmlFor={labels.gender} className={styles.label}>
            {labels.gender}
          </label>
          <RadioButton name="gender" options={labels.genderValues} control={control} />
          {formState?.errors?.gender?.message && (
            <span className={styles?.errorMessage}>{formState?.errors?.gender?.message}</span>
          )}
        </div>
        <div className={styles.formWrapper}>
          {isExternalState ? (
            <React.Fragment>
              <TextInput
                value={EXTERNAL_STATE[0].toUpperCase() + EXTERNAL_STATE.slice(1).toLowerCase()} // capitalize first letter
                disabled
                label={labels.county}
                className={styles.inputSpacer}
              />
              <SelectInput
                control={control}
                name="cityOfBirth"
                label={labels.birthNationality}
                options={nationality}
                hasMarginBottom
                onChange={() => {
                  setValue('dynamicInputs', []);
                  setIsFiscalCodeToggleVisible(false);
                  if (isFiscalCodeCalculated) {
                    dispatch(setIsCalculatedFiscalCode(false));
                  }
                }}
              />
            </React.Fragment>
          ) : (
            <React.Fragment>
              <SelectInput
                control={control}
                label={labels.county}
                name="provinceOfBirth"
                onChange={(element) => {
                  const townShipsWithoutDuplicate = getMunicipality(parsedCSVResponse, element.target.value);
                  setTownshipsOfBirth([
                    ...DEFAULT_TOWNSHIP_SELECTION,
                    ...townShipsWithoutDuplicate
                      .map((item) => ({
                        value: item,
                        label: item,
                      }))
                      .sort((a, b) => a.label.localeCompare(b.label)),
                  ]);
                  setValue('cityOfBirth', EMPTY_ITEM_DEFAULT.value);
                  setValue('dynamicInputs', []);
                  setIsFiscalCodeToggleVisible(false);
                  if (isFiscalCodeCalculated) {
                    dispatch(setIsCalculatedFiscalCode(false));
                  }
                }}
                options={municipalities}
                className={styles.inputSpacer}
                hasMarginBottom
              />
              <SelectInput
                control={control}
                name="cityOfBirth"
                label={labels.city}
                options={townshipsOfBirth}
                hasMarginBottom
                onChange={() => {
                  setValue('dynamicInputs', []);
                  setIsFiscalCodeToggleVisible(false);
                  if (isFiscalCodeCalculated) {
                    dispatch(setIsCalculatedFiscalCode(false));
                  }
                }}
              />
            </React.Fragment>
          )}
        </div>
        <SimpleCheckbox
          value={EXTERNAL_STATE}
          label={labels.externalState}
          checked={isExternalState}
          className={personalDataStyle.sameAsDomicile}
          handleFilterChange={(e) => {
            dispatch(setIsExternalState(!isExternalState));
            if (!e.checked) {
              setValue('provinceOfBirth', EMPTY_ITEM_DEFAULT.value);
            } else {
              setValue('provinceOfBirth', 'EE');
            }
            setIsFiscalCodeToggleVisible(false);
            setValue('dynamicInputs.0.isFiscalCodeCorrect', false);
            setValue('cityOfBirth', EMPTY_ITEM_DEFAULT.value);
          }}
        />
        <TextInput
          control={control}
          name="fiscalCode"
          label={labels.fiscalCode}
          hasMarginTop
          placeholder={labels.labelCalcolaCodiceFiscale}
          onChange={(e) => {
            e.target.value = e.target.value.toUpperCase();
            setValue('dynamicInputs', []);
            setIsFiscalCodeToggleVisible(false);
            if (isFiscalCodeCalculated) {
              dispatch(setIsCalculatedFiscalCode(false));
            }
          }}
        />
        <div className={classNames(styles.formWrapper, personalDataStyle['fiscalCode-wrapper'])}>
          <div className={personalDataStyle['fiscalCode-toggler']}>
            {isFiscalCodeToggleVisible ? (
              <ToggleCheckBox
                control={control}
                name="dynamicInputs.0.isFiscalCodeCorrect"
                value="Il codice fiscale è corretto"
                onChange={() => {
                  errorFC && clearErrors('fiscalCode');
                  setValue('dynamicInputs', []);
                }}
              />
            ) : (
              <span />
            )}
          </div>
          <button
            type="button"
            disabled={isCalculateFiscalCodeDisabled}
            className={personalDataStyle['fiscalCode-compute']}
            onClick={() => computeFiscalCode()}
          >
            {labels.labelCalcolaCodiceFiscale}
          </button>
        </div>
        <TextInput
          control={control}
          name="address"
          label={labels.address}
          placeholder={labels.placeholderAddress}
          hasMarginBottom
        />
        <div className={styles.formWrapper}>
          {!isExternalResidencial ? (
            <SelectInput
              control={control}
              label={labels.residenceCounty}
              name="province"
              options={municipalities}
              onChange={(element) => {
                const townShipsWithoutDuplicate = getMunicipality(parsedCSVResponse, element.target.value);
                setTownships([
                  ...DEFAULT_TOWNSHIP_SELECTION,
                  ...townShipsWithoutDuplicate
                    .map((item) => ({
                      value: item,
                      label: item,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label)),
                ]);
                setValue('city', EMPTY_ITEM_DEFAULT.value);
              }}
              className={styles.inputSpacer}
              hasMarginBottom
            />
          ) : (
            <TextInput
              value={EXTERNAL_STATE[0].toUpperCase() + EXTERNAL_STATE.slice(1).toLowerCase()} // capitalize first letter
              disabled
              label={labels.residenceCounty}
              className={styles.inputSpacer}
              hasMarginBottom
            />
          )}

          <SelectInput
            control={control}
            name="city"
            label={!isExternalResidencial ? labels.residenceCity : labels.nationality}
            options={!isExternalResidencial ? townships : nationality}
            className={styles.inputSpacer}
            hasMarginBottom
          />
          <TextInput control={control} name="cap" label={labels.zipCode} placeholder="CAP" hasMarginBottom />
        </div>
        <div className={personalDataStyle.checkboxContainer}>
          <SimpleCheckbox
            value={isSameAsDomicile.toString()}
            label={labels.sameAsDomicile}
            checked={isSameAsDomicile}
            className={personalDataStyle.sameAsDomicile}
            handleFilterChange={(target) => {
              const isChecked = target.value === 'true';
              dispatch(setIsSameAsResidence(!isChecked));
              if (isChecked) {
                appendDomicile(
                  {
                    domicileAddress: domicileAddress || '',
                    domicileCity: domicileCity || EMPTY_ITEM_DEFAULT.value,
                    domicileProvince: domicileProvince || EMPTY_ITEM_DEFAULT.value,
                    domicileCap: domicileCap || '',
                  },
                  {
                    shouldFocus: true,
                    focusName: 'domicileAddress',
                  }
                );
              } else {
                removeDomicile(0);
              }
            }}
          />
          <SimpleCheckbox
            value={EXTERNAL_STATE}
            label={labels.externalResidential}
            checked={isExternalResidencial}
            className={personalDataStyle.sameAsDomicile}
            handleFilterChange={(e) => {
              dispatch(setIsExternalResidencial(!isExternalResidencial));
              if (!e.checked) {
                setValue('province', EMPTY_ITEM_DEFAULT.value);
              } else {
                setValue('province', 'EE'); // TODO capire
              }
              setValue('city', EMPTY_ITEM_DEFAULT.value);
              setValue('cap', '');
            }}
          />
        </div>

        {!isSameAsDomicile && (
          <React.Fragment>
            <div className={styles.formWrapper}>
              <TextInput
                control={control}
                name="dynamicDomicile.0.domicileAddress"
                label={labels.addressDomicile}
                placeholder={labels.placeholderDomicileAddress}
                hasMarginTop
              />
            </div>
            <div className={`${styles.formWrapper} ${styles.formDomicileWrapper}`}>
              <SelectInput
                control={control}
                label={labels.provinceDomicile}
                name="dynamicDomicile.0.domicileProvince"
                onChange={(element) => {
                  const townShipsWithoutDuplicate = getMunicipality(parsedCSVResponse, element.target.value);
                  setTownshipsOfDomicile([
                    ...DEFAULT_TOWNSHIP_SELECTION,
                    ...townShipsWithoutDuplicate
                      .map((item) => ({
                        value: item,
                        label: item,
                      }))
                      .sort((a, b) => a.label.localeCompare(b.label)),
                  ]);
                  setValue('dynamicDomicile.0.domicileCity', EMPTY_ITEM_DEFAULT.value);
                }}
                options={municipalities}
                className={styles.inputSpacer}
                hasMarginBottom
              />
              <SelectInput
                control={control}
                name="dynamicDomicile.0.domicileCity"
                label={labels.cityDomicile}
                options={townshipsOfDomicile}
                className={styles.inputSpacer}
                hasMarginBottom
              />
              <TextInput
                control={control}
                name="dynamicDomicile.0.domicileCap"
                label={labels.zipCodeDomicile}
                placeholder={labels.placeholderZipCodeDomicile}
                hasMarginBottom
              />
            </div>
          </React.Fragment>
        )}
        {/* isNextDisabled={!formState.isValid} */}
        <ButtonRegistrationWrapper handleGoBack={back!}></ButtonRegistrationWrapper>
      </form>
    </div>
  );
};
